// src/components/CategoryList.js
import React, { useState } from 'react';
import { addCategory as apiAddCategory, updateCategory as apiUpdateCategory, deleteCategory as apiDeleteCategory } from '../config/api';
import { PlusCircle, Edit2, Trash2 } from 'lucide-react';
import { useStore } from '../context/StoreContext';

function CategoryList() {
  const { categories, addCategory, updateCategory, deleteCategory } = useStore();
  const [name, setName] = useState('');
  const [label, setLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editName, setEditName] = useState('');


  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!name || !label) {
      alert('Both name and label are required');
      return;
    }

    setLoading(true);
    try {
      const newCategory = await apiAddCategory({ name, label });
      addCategory(newCategory);
      setName('');
      setLabel('');
    } catch (error) {
      console.error('Error adding category', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editLabel) {
      alert('Label is required');
      return;
    }

    setLoading(true);
    try {
      const updatedCategory = await apiUpdateCategory(editId, { 
        label: editLabel, 
        name: editName 
      });
      updateCategory(editId, updatedCategory);
      setEditId(null);
      setEditLabel('');
      setEditName('');
    } catch (error) {
      console.error('Error updating category', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await apiDeleteCategory(id);
        deleteCategory(id);
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };

  return (
    <div className="overflow-hidden bg-white shadow-md rounded-xl">
      <div className="p-7">
        <h2 className="mb-6 text-xl font-semibold text-amber-900 font-bebas">Categories</h2>
        
        {/* Add Category Form */}
        <form onSubmit={handleAddSubmit} className="mb-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="block w-full px-3 py-2 mt-1 border rounded-lg border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Label</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="block w-full px-3 py-2 mt-1 border rounded-lg border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center justify-center w-full px-4 py-2 text-white border border-transparent rounded-lg shadow-sm bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
          >
            <PlusCircle className="w-5 h-5 mr-2" />
            {loading ? 'Adding...' : 'Add Category'}
          </button>
        </form>

        {/* Categories List */}
        <div className="space-y-3">
          {categories.map((category) => (
            <div
              key={category._id}
              className="p-4 transition-colors border rounded-lg border-amber-200 hover:bg-orange-50"
            >
              {editId === category._id ? (
                <form onSubmit={handleEditSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Edit Label</label>
                    <input
                      type="text"
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      className="block w-full px-3 py-2 mt-1 border rounded-lg border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Edit Name</label>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="block w-full px-3 py-2 mt-1 border rounded-lg border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      required
                    />
                  </div>
                  <div className="flex space-x-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center justify-center flex-1 px-4 py-2 text-white border border-transparent rounded-lg shadow-sm bg-amber-600 hover:bg-amber-700"
                    >
                      {loading ? 'Updating...' : 'Update'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditId(null);
                        setEditLabel('');
                        setEditName('');
                      }}
                      className="inline-flex items-center justify-center flex-1 px-4 py-2 text-white bg-gray-600 border border-transparent rounded-lg shadow-sm hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900">{category.label}</span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setEditId(category._id);
                        setEditLabel(category.label);
                        setEditName(category.name);
                      }}
                      className="p-1 text-amber-600 hover:text-amber-700"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => {handleDelete(category._id)}}
                      className="p-1 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default CategoryList;